import {Link} from "react-router-dom";
import {PhoneHQAvatar, PhoneHQIconAvatar, PhoneHQButton, PhoneHQGlyph, PhoneHQInput, PhoneHQConfirmDialog, PhoneHQRadioCards, PhoneHQSwitch, PhoneHQActionDialog} from 'ch3-ui-lib';
import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import AddContactModal from "../Contacts/AddContactModal";
import {validateEmail, validateName} from "../../utilis/commonValidators";
import {useNavigate} from 'react-router-dom';
import {useParams} from 'react-router-dom';
import useMediaQuery from "../../utilis/screenWidthUtils.ts";
import {IconPicker} from "../../components/IconPicker/IconPicker";
import { Colors } from "../../utilis/types/Types";
import {TooltipPrimitive as Tooltip} from "ch3-ui-lib";
import {Info} from "lucide-react";
import {getFormatedPhoneNumber} from "../../utilis/msisdnUtilis";

import {
    useFetchMsisdnSettingsInfoQuery,
    useUpdateMsisdnSettingsInfoMutation
} from "../../store/features/MsisdnSettings/msisdnSettingsService";
import {RecordingMode} from "../../store/features/Msisdns/msisdnService";
import {User} from "../../store/features/Users/userService";
import {Group} from "../../store/features/groups/groupsService";
import {showToast} from "../../store/features/Notifications/notificationsSlice.tsx";
import {useAppDispatch} from "../../store/store.ts";


interface PhoneNumberInfoProps {
    admin: boolean;
    /** Tells the settings drawer whether leaving would drop edits (SettingsPhone-Unsaved). */
    onDirtyChange?: (dirty: boolean) => void;
    /** Cancel goes through the drawer so it can ask first. */
    onCancel?: () => void;
}

export const NAME_MAX = 150;

/** What a saved form looks like, to tell whether anything changed. */
const formSnapshot = (data: object) => JSON.stringify(data);

export const PhoneNumberInfo = ({admin = false, onDirtyChange, onCancel}:PhoneNumberInfoProps) => {
    const dispatch = useAppDispatch();
    const role = useSelector((state: any) => state.auth.role);
    const [icon, setIcon] = useState('');
    const [color, setColor] = useState('');

    const {id} = useParams<{ id: string }>();
    const isMobile = useMediaQuery('(max-width: 960px)');

    const navigate = useNavigate();
    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

    const [updateInfo] = useUpdateMsisdnSettingsInfoMutation();
    const { data: msisdnInfo } = useFetchMsisdnSettingsInfoQuery(id as string);


    const [users, setUsers] = useState([] as User[]);
    const [groups, setGroups] = useState([] as Group[])
    const pickerRef = useRef<HTMLDivElement>(null);
    const [formData, setFormData] = useState({

        msisdn: {
            label: msisdnInfo?.msisdn?.label ?? '',
            icon: msisdnInfo?.msisdn?.icon ?? 'home',
            iconColor: msisdnInfo?.msisdn?.iconColor ?? 'blue',
            msisdn: msisdnInfo?.msisdn?.msisdn ?? 0,
            msisdnId: msisdnInfo?.msisdn?.msisdnId ?? 0,
            type: msisdnInfo?.msisdn?.type ?? '',
        },
        emailsSetting: {
            enabled: msisdnInfo?.emailsSetting?.enabled ?? false,
            email:  msisdnInfo?.emailsSetting?.email ?? null
        },
        callRecordingEnabled: msisdnInfo?.callRecordingEnabled ?? false,
        recordingMode: msisdnInfo?.recordingMode ?? RecordingMode.USER_CONTROLLED,
        users: msisdnInfo?.users?.length ? [...msisdnInfo?.users] : [],
        groups: msisdnInfo?.groups?.length ? [...msisdnInfo?.groups] : [],
    })
    const [errors, setErrors] = useState({
        name: '',
        icon: '',
        email: '',
    });

    const handleChange = (field:string, value: string) => {
        const newErrors = { ...errors };

        if (field === 'label') {
            if (value.trim().length > NAME_MAX) {
                newErrors.name = `Name is too long. Use up to ${NAME_MAX} characters (now ${value.trim().length}).`;
            } else {
                newErrors.name = validateName(value) ? '' : 'Name is required';
            }
        }

        if (field === 'icon') {
            newErrors.icon = validateName(value) ? '' : 'Icon is required';
        }

        if (field === 'email') {
            newErrors.email = formData.emailsSetting.enabled ? validateEmail(value) ? '' : 'Email is required' : '';
        }

        setErrors(newErrors);
        setFormData((prev) => ({...prev, msisdn: {...prev.msisdn, label: value}}));
    }

    const savedSnapshot = useRef('');

    useEffect(() => {
        const loaded = {
            msisdn: {
                label: msisdnInfo?.msisdn?.label ?? '',
                icon: msisdnInfo?.msisdn?.icon ?? 'home',
                iconColor: msisdnInfo?.msisdn?.iconColor ?? 'blue',
                msisdn: msisdnInfo?.msisdn?.msisdn ?? 0,
                msisdnId: msisdnInfo?.msisdn?.msisdnId ?? 0,
                type: msisdnInfo?.msisdn?.type ?? '',
            },
            emailsSetting: {
                enabled: msisdnInfo?.emailsSetting?.enabled ?? false,
                email: msisdnInfo?.emailsSetting?.email ?? null
            },
            callRecordingEnabled: msisdnInfo?.callRecordingEnabled ?? false,
            recordingMode: msisdnInfo?.recordingMode ?? RecordingMode.USER_CONTROLLED,
            users: msisdnInfo?.users?.length ? [...msisdnInfo?.users] : [],
            groups: msisdnInfo?.groups?.length ? [...msisdnInfo?.groups] : [],
        };
        savedSnapshot.current = formSnapshot(loaded);
        setFormData(loaded);
        setIcon(msisdnInfo?.msisdn?.icon ?? 'home');
        setColor(msisdnInfo?.msisdn?.iconColor ?? 'blue');
        setGroups(msisdnInfo?.groups ?? []);
        setUsers(msisdnInfo?.users ?? []);
    }, [msisdnInfo]);

    const dirty = Boolean(msisdnInfo) && savedSnapshot.current !== '' && formSnapshot(formData) !== savedSnapshot.current;
    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    const handleValidation = () => {
        const newErrors = {
            name: formData.msisdn.label.trim().length > NAME_MAX
                ? `Name is too long. Use up to ${NAME_MAX} characters (now ${formData.msisdn.label.trim().length}).`
                : validateName(formData.msisdn.label) ? '' : 'Name is required',
            icon: validateName(formData.msisdn.icon) ? '' : 'Icon is required',
            email: formData.emailsSetting.enabled ? validateEmail(formData.emailsSetting.email) ? '' : 'Email is required' : '',
        };
        setErrors(newErrors);
        return newErrors;
    };

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!formData.emailsSetting.enabled && formData.emailsSetting.email == '') {
            setFormData((prev) => ({...prev, emailsSetting: {...prev.emailsSetting, email: null}}));
        }
        const fieldErrors = Object.values(handleValidation());
        const hasFieldErrors = fieldErrors.some(fieldError => {
            if (Array.isArray(fieldError)) {
                return fieldError.some(error => error !== '');
            } else {
                return fieldError !== '';
            }
        });

        if (hasFieldErrors) return;
        const body = {
            ...formData,
            users: formData.users.map((user) => user.userId),
            groups: formData.groups.map((group) => group.groupId),
        }

        if (!id) return;
        try {
            await updateInfo({id , body}).unwrap();
            dispatch(showToast({
                description: 'Phone number settings updated successfully',
                type: 'success',
                position: 'top-right',
                icon: 'check'
            }))
            navigate( (admin ? '/admin' : '' ) +  '/phoneNumbers');

        } catch (error: any) {
            const message = error?.data?.message ?? 'Failed to update phone number settings';
            dispatch(showToast({
                description: message,
                type: 'error',
                position: 'top-right',
                icon: 'error'
            }))
        }
    };

    const handleClickOutside = (event: any) => {
        if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
            setEmojiPickerOpen(false);
        }
    };
    useEffect(() => {
        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [emojiPickerOpen]);

    const handleMenageAccessChange = (res: boolean) => {

        if (!res) {
            setUsers([...formData.users]);
            setGroups([...formData.groups]);
        }

        const data = {
            ...formData,
            users: users,
            groups: groups,
        };

        setFormData(data);
    };

    const menageAccessTrigger = <PhoneHQButton variant="secondary" size="sm" disabled={msisdnInfo?.msisdn.assigmentScope == 'PRIVATE'} className='mb-2' phq-id='phoneNumberDetails-manageAccessTriggerBtn' ><PhoneHQGlyph name='settings' size={16}/>Manage access</PhoneHQButton>
    const modalContent = <AddContactModal selectedUsers={formData.users}
                                          admin={admin}
                                          selectedGroups={formData.groups} setUsers={setUsers}
                                          setGroups={setGroups}/>

    const handleNumberAvatar = (res:boolean) => {
        if(!res) return;
        setFormData((prev) => ({...prev, msisdn: {...prev.msisdn, icon: icon, iconColor:color}}));
        setErrors({...errors, icon: ''})
    };


    const manageIconContent = <IconPicker setIcon={setIcon} setColor={setColor} currentIcon={formData.msisdn.icon} currentColor={formData.msisdn.iconColor}/>
    // Sits on the input's centre line and the label's left edge; the ring marks it as clickable.
    const iconModalTrigger = (
        <div className={`mr-3 flex cursor-pointer rounded-control hover:ring-2 hover:ring-brand-tint ${errors.icon ? 'ring-2 ring-danger' : ''}`}>
            <PhoneHQIconAvatar icon={formData.msisdn.icon} color={formData.msisdn.iconColor} size="32px"/>
        </div>
    );


    const [restrictedOpen, setRestrictedOpen] = useState(false);

    const handleCallRecordingChange = () => {
        if (!canManageRecordingMode && formData.recordingMode === RecordingMode.RESTRICTED) {
            setRestrictedOpen(true);
            return;
        }
        setFormData((prev) => ({...prev, callRecordingEnabled: !formData.callRecordingEnabled}));
    }

    const handleRecordingModeChange = (value: string) => {
        setFormData((prev) => ({...prev, recordingMode: value as RecordingMode}));
    }

    const canManageRecordingMode = admin || role === 'ADMIN' || role === 'SUPERADMIN';
    const isRestricted = formData.recordingMode === RecordingMode.RESTRICTED;
    return (
        <>{ msisdnInfo && !isMobile &&
            <div className={`${isMobile ? ' px-4' : ' '}`}>
                <div className='h-[calc(100vh-63px-var(--header-height))] overflow-x-hidden   overflow-y-auto '>
                    <div className={`flex flex-col gap-3 pb-6 ${isMobile ? ' my-6' : ''}`}>
                        <div>

                      <div className='text-body-strong text-ink'>
                          General information
                      </div>
                      <div className='text-ink-muted  pb-6 text-sm '>
                      Update phone number's name
                      </div>
                  </div>
                  <div className='grid w-full gap-4 rounded-card border border-solid border-line bg-surface p-4'>
                      <div className={'flex flex-col '}>
                            <p className="text-sm mb-1">Name</p>
                          <div className={'flex  flex-col'}>
                            <div className="flex items-center">
                                  <PhoneHQActionDialog title={'Icon and colour'} trigger={iconModalTrigger} confirmLabel='Apply' cancelLabel='Cancel' onConfirm={() => handleNumberAvatar(true)} onCancel={() => handleNumberAvatar(false)}>{manageIconContent}</PhoneHQActionDialog>

                              {/* </div> */}
                              <div className="w-full">

                                  <PhoneHQInput 
                                   phq-id='phoneNumberDetails-numberLabelInput'  value={formData.msisdn.label}
                                           onChange={({target: {value: val}}) => {
                                               handleChange('label', val);
                                           }} error={errors.name}
                                           containerClassName='max-w-full py-3 pr-4 text-sm' />
                              </div>
                              </div>
                              <div className={`mb-5 ml-auto text-footnote ${formData.msisdn.label.trim().length > NAME_MAX ? 'text-danger-ink' : 'text-ink-muted'}`}>{formData.msisdn.label.length}/{NAME_MAX}</div>


                          </div>
                      </div>


                  </div>
              </div>


                    <div className={`flex flex-col gap-3 pb-6 ${isMobile ? ' my-6' : ' '}`}>


                        <div>

                            <div className='flex flex-col gap-2'>
                                <div>
                                    <div className='text-body-strong text-ink'>
                                        Share access
                                    </div>
                                    <div className='pr-6 text-ink-muted text-sm py-5'>
                                        Give other members in your workspace access to this phone number
                                    </div>
                                </div>
                            <div>
                                {msisdnInfo.msisdn.assigmentScope === 'PRIVATE' ? (
                                    <PrivateAccessCard number={getFormatedPhoneNumber(msisdnInfo.msisdn.msisdn, true)}/>
                                ) : (
                                    <PhoneHQActionDialog title={'Share access'} trigger={menageAccessTrigger} confirmLabel='Confirm' cancelLabel='Cancel' onConfirm={() => handleMenageAccessChange(true)} onCancel={() => handleMenageAccessChange(false)}>{modalContent}</PhoneHQActionDialog>
                                )}
                                </div>

                            </div>


                        </div>
                        <div className='flex max-h-[320px] w-full flex-col overflow-y-auto rounded-card border border-solid border-line bg-surface'>


                            {Boolean(formData.users?.length) && <>
                                <div className={`p-6 pl-8 border-b  border-line `}>
                                    Users
                                </div>
                                <div>
                                    {(formData.users
                                        ? [...formData.users]
                                            .sort((a, b) =>
                                                (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName)
                                            )
                                            .map((user, index) => {
                                                return (
                                                    <div className={`border-b border-line`}>
                                                        <div className='flex gap-3 items-center px-4 py-3'
                                                             key={`user-${user.userId ?? index}`}>
                                                            <PhoneHQAvatar src={user.avatarUrl} name={user.firstName + ' ' + user.lastName} size="40px"/>
                                                            {user.firstName + ' ' + user.lastName}
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        : null)
                                    }
                                </div>

                            </>}
                            {Boolean(formData.groups?.length) && <>
                                <div className={`p-6 pl-8 border-b   border-line`}>
                                    Groups
                                </div>
                                <div>
                                    {formData.groups
                                        ?.sort((a, b) => a.name.localeCompare(b.name))
                                        .map((group, index) => {
                                            return (
                                                <div key={`group-${group.id ?? index}`}
                                                     className={`border-b  w-full  border-line`}>
                                                    <div className='flex gap-3 items-center px-4 py-3'>
                                                        <PhoneHQIconAvatar icon={group.icon ?? 'star'} color={group.iconColor ?? Colors.orange} size="40px"/>{group.name}
                                                    </div>
                                                </div>
                                            )
                                        }
                                    )}

                                </div>
                            </>
                            }
                        </div>

                    </div>
                    <div className='mr-[384px] w-full'>
                        <div className="flex flex-col gap-3 pb-6">
                            <div>
                                <div className='text-body-strong text-ink'>
                                    Call  settings
                                </div>
                                <div className='mt-1 text-footnote text-ink-muted'>
                                    Configure how your incoming and outgoing calls are recorded and stored.
                                </div>
                            </div>
                            {/*    <div className='mx-6 py-6 border-b border-gray-900'>*/}
                            {/*        <PhoneHQSwitch*/}
                            {/*                checked={formData.emailsSetting.enabled}*/}
                            {/*                onCheckedChange={handleEmailNotificationChange}*/}
                            {/*                label={'Missed call'}*/}
                            {/*                description={'Set where you want to receive notifications about missed calls'}/>*/}
                            {/*        { formData.emailsSetting.enabled &&*/}
                            {/*            <div className='flex gap-3 mx-6 py-6 '>*/}
                            {/*                <PhoneHQInput value={formData.emailsSetting.email ?? undefined}*/}
                            {/*                         onChange={({target: {value: val}}) => handleEmailChange(val)}*/}
                            {/*                         error={errors.email}*/}
                            {/*                         containerClassName='max-w-full' label='Mails'/>*/}

                            {/*            </div>}*/}
                            {/*    </div>*/}
                            <div className='grid w-full gap-4 rounded-card border border-solid border-line bg-surface p-4'>
                                <div
                                    onClick={() => {
                                        if (!canManageRecordingMode && isRestricted) {
                                            setRestrictedOpen(true);
                                        }
                                    }}
                                >
                                    <PhoneHQSwitch
                                            checked={formData.callRecordingEnabled}
                                            onCheckedChange={handleCallRecordingChange}
                                            label={'Call recording'}
                                            description={'With this option on, both incoming and outgoing calls will be recorded.'}/>
                                </div>
                                {!canManageRecordingMode && isRestricted && (
                                    <PhoneHQConfirmDialog
                                        open={restrictedOpen}
                                        onOpenChange={setRestrictedOpen}
                                        title="Recording managed by administrator"
                                        confirmLabel="Got it"
                                        onConfirm={() => setRestrictedOpen(false)}
                                        width={440}
                                    >
                                        <div className='flex flex-col gap-3'>
                                            <p className='m-0'>Call recording on this number is managed by your administrator.</p>
                                            <p className='m-0 font-medium text-ink'>Current setting: Recording {formData.callRecordingEnabled ? 'ON' : 'OFF'}</p>
                                            <p className='m-0'>This ensures compliance with company policies and regulatory requirements. All calls on this number are automatically recorded.</p>
                                            <p className='m-0'>Questions? Contact your administrator.</p>
                                        </div>
                                    </PhoneHQConfirmDialog>
                                )}

                                {canManageRecordingMode && (
                                    <div className='flex flex-col gap-3 pt-4 pl-3'>
                                        <div className='flex items-center '>
                                            <div className='text-md font-semibold'>Recording mode</div>
                                            <div data-tooltip="When restricted, users will see that recording is managed by the administrator and won't be able to change this setting." >
                                                <PhoneHQGlyph name='info' size={20} className='text-ink-muted cursor-help'/>
                                            </div>
                                        </div>
                                        <div>
                                            <PhoneHQRadioCards
                                                label="Recording mode"
                                                value={formData.recordingMode}
                                                onChange={handleRecordingModeChange}
                                                options={[
                                                    {
                                                        value: RecordingMode.USER_CONTROLLED,
                                                        label: 'User-controlled (default)',
                                                        description: 'Users can enable or disable call recording',
                                                    },
                                                    {
                                                        value: RecordingMode.RESTRICTED,
                                                        label: 'Restricted',
                                                        description: 'Only administrators can manage call recording',
                                                    },
                                                ]}
                                            />
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                    <div className={`flex justify-end`}>
                        <div className='flex gap-3 justify-end pt-2'>
                            {onCancel
                                ? <PhoneHQButton variant="secondary" size="lg" onClick={onCancel} phq-id='phoneNumberDetails-cancelBtn' >Cancel</PhoneHQButton>
                                : <Link to={'/phoneNumbers'}><PhoneHQButton variant="secondary" size="lg" phq-id='phoneNumberDetails-cancelBtn' >Cancel</PhoneHQButton></Link>}
                            {/* SettingsPhone-NameError: no saving while the name is too long. */}
                            <PhoneHQButton size="lg" disabled={Boolean(errors.name)} onClick={(e) => handleSubmit(e)} phq-id='phoneNumberDetails-saveChangesBtn' >Save changes</PhoneHQButton>
                        </div>
                    </div>
                </div>
            </div>
        }

        </>
    )
}

/** SettingsPhone-Private: who can use the number, with why it can't be shared. */
const PrivateAccessCard = ({number}: { number: string }) => (
    <div className="flex items-center gap-3 rounded-xl border border-solid border-line px-4 py-3.5">
        <span className="flex-1">
            <span className="block text-callout text-ink">Access: Only me</span>
            <span className="text-footnote text-ink-muted">{number} · Private</span>
        </span>
        <Tooltip.Provider delayDuration={200}>
            <Tooltip.Root>
                <Tooltip.Trigger asChild>
                    <button type="button" aria-label="Why can't I share this number?" className="flex cursor-help border-0 bg-transparent p-1 text-ink-muted">
                        <Info size={16} aria-hidden="true" className="!p-0 !text-current"/>
                    </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="top" sideOffset={6} className="z-[60] whitespace-nowrap rounded-chip bg-zinc-950 px-2.5 py-1.5 text-footnote text-white shadow-menu">
                        Sharing is not currently enabled for this number.
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </Tooltip.Provider>
    </div>
);
